import React from 'react';
import { CheckCircle, MapPin, Building, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const MombasaNairobiPage = () => {
  const services = [
    'In-Line Inspection (ILI) for pipeline integrity assessment',
    'Material Grade Determination (MGD) for pipeline specifications',
    '20-inch pipeline diameter inspection',
    '450km pipeline length coverage from Mombasa to Nairobi',
    'Comprehensive integrity data collection and analysis',
    'Pipeline condition assessment and reporting'
  ];

  const keyStats = [
    { value: '450km', label: 'Pipeline Length' },
    { value: '20"', label: 'Pipeline Diameter' },
    { value: '2018', label: 'Commissioned Year' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/18784617/pexels-photo-18784617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Mombasa-Nairobi Pipeline Inspection
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">Kenya</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              <span className="text-lg">Kenya Pipeline Company</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="text-lg">2018-2020</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-red-600">Overview</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              In-Line Inspection (ILI) and Material Grade Determination (MGD) for 20-inch, 450km pipeline from 
              Mombasa to Nairobi. Commissioned in 2018.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {keyStats.map((stat, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">{stat.value}</div>
                  <div className="text-red-100">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Inspection Services */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Pipeline Inspection Services
            </h3>
            <p className="text-slate-700 mb-6">
              Total Vision provided comprehensive pipeline inspection services for Kenya's critical Mombasa-Nairobi pipeline infrastructure:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{service}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">
                In-Line Inspection (ILI)
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                In-Line Inspection is a critical technology for assessing pipeline integrity without interrupting operations. 
                Our ILI services for the Mombasa-Nairobi pipeline provide comprehensive data on pipeline condition, detecting 
                corrosion, cracks, dents, and other anomalies that could affect pipeline safety and operational efficiency.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                The 450km inspection coverage ensures complete visibility into the pipeline's structural integrity, enabling 
                proactive maintenance planning and risk mitigation strategies.
              </p>
            </div>
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4">Key Deliverables</h4>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Comprehensive ILI data collection and analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Material Grade Determination (MGD) for pipeline segments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Pipeline integrity assessment reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Anomaly detection and classification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Maintenance recommendations and prioritization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Pipeline condition trending and lifecycle analysis</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Project Significance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Project Significance</h3>
            <p className="text-lg text-slate-700 leading-relaxed">
              The Mombasa-Nairobi pipeline represents a critical energy infrastructure asset for Kenya, connecting the 
              coastal port of Mombasa to the capital city of Nairobi. Total Vision's comprehensive In-Line Inspection and 
              Material Grade Determination services ensure the continued safe and reliable operation of this vital 450km, 
              20-inch pipeline. Our advanced inspection technologies and expertise provide Kenya Pipeline Company with the 
              data and insights necessary to maintain pipeline integrity, optimize maintenance programs, and ensure 
              uninterrupted fuel supply to the nation's economic heartland.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MombasaNairobiPage;
